import React, { useEffect, useRef, useState } from 'react';
import { useCurrentUser } from '@mp/hooks';
import { useQueryParams, JsonParam, StringParam } from 'use-query-params';
import { useProNavigate } from '@mp/contexts';
import { MultiFactorError, MultiFactorResolver } from 'firebase/auth';

import {
  Box,
  Form,
  Heading,
  NumberField,
  Text,
  SubmitButton,
  Label,
  useFirebaseContext,
  FirebaseAuthError,
  TextField,
  VStack,
  HStack,
  Button,
} from '@medely/ui-kit';
import { FixedFooterLayout } from '@mp/components';
import { HelpCenterLink } from '@mp/components/helpCenterLink';
import { toPhoneNumber } from '@mp/utils/toPhoneNumber';
import {
  mfaPasswordEntrySchema,
  mfaSmsCodeEntrySchema,
  MfaSmsCodeEntrySchemaProps,
  MfaSmsPasswordEntrySchemaProps,
} from './schema';

import { MfaErrorAlert } from './components';
import { useMFAEnrollment } from '@mp/hooks/useMFAEnrollment/useMFAEnrollment.web';
import { MFAFlowType } from './utils/types';
import { useApplicationMutations } from '../application/applicationFlowMutations';
import { FirebaseErrorHandler } from '@mp/utils/firebase/firebaseErrorHandler';

export const MfaSmsCodeEntryScreen = () => {
  const [queryParams] = useQueryParams({
    forward: JsonParam,
    firebaseError: JsonParam,
    flow: StringParam,
  });

  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [verificationId, setVerificationId] = useState('');
  const [needsReauth, setNeedsReauth] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [challengeResponse, setChallengeResponse] = useState<{
    verificationId: string;
    resolver: MultiFactorResolver;
  }>();

  const { currentUser } = useCurrentUser();
  const { reauthenticateUser } = useFirebaseContext();

  const [maskedPhoneNumber, setMaskedPhoneNumber] = useState<string>();
  const phoneNumber =
    maskedPhoneNumber ?? toPhoneNumber(currentUser?.phone_number || '') ?? undefined;

  const { navigate } = useProNavigate();
  const {
    enrollPhoneMfa,
    handleMfaChallenge,
    resolveMfaSignIn,
    startEnrollment,
    loading,
    error,
    getUserPhoneNumberMasked,
  } = useMFAEnrollment();

  const flowType = (queryParams.flow as MFAFlowType) || 'SENSITIVE_SCREEN';

  useEffect(() => {
    (async () => {
      if (!queryParams.firebaseError) {
        const verificationId = await startEnrollment(currentUser?.phone_number || '');
        if (verificationId) {
          setVerificationId(verificationId);
        }
      }
    })();
  }, [queryParams.firebaseError]);

  useEffect(() => {
    (async () => {
      if (queryParams.firebaseError) {
        const mfaResponse = await handleMfaChallenge(queryParams.firebaseError as MultiFactorError);
        const maskedNumber = getUserPhoneNumberMasked(
          queryParams.firebaseError as MultiFactorError,
        );
        if (maskedNumber) {
          setMaskedPhoneNumber(maskedNumber);
        }
        if (mfaResponse) {
          const { resolver, verificationId } = mfaResponse;
          setChallengeResponse({ resolver, verificationId });
        }
      }
    })();
  }, [queryParams.firebaseError]);

  const onResendCode = async () => {
    if (queryParams.firebaseError) {
      const mfaResponse = await handleMfaChallenge(queryParams.firebaseError);
      const maskedNumber = getUserPhoneNumberMasked(queryParams.firebaseError as MultiFactorError);
      if (maskedNumber) {
        setMaskedPhoneNumber(maskedNumber);
      }
      if (mfaResponse) {
        const { resolver, verificationId } = mfaResponse;
        setChallengeResponse({ resolver, verificationId });
      }
    } else if (currentUser?.phone_number) {
      const verificationCode = await startEnrollment(currentUser.phone_number);
      if (verificationCode) {
        setVerificationId(verificationCode || '');
        setNeedsReauth(false);
        setErrorMessage('');
      }
    }
  };

  useEffect(() => {
    if ((error as MultiFactorError)?.code === 'auth/invalid-verification-code') {
      setErrorMessage('The provided verification code is invalid');
    }
  }, [error]);

  const resendSms = async () => {
    setCanResend(false);
    timerRef.current = setTimeout(() => setCanResend(true), 30_000);
    await onResendCode();
  };

  useEffect(() => {
    if (
      (error as MultiFactorError)?.code &&
      FirebaseErrorHandler.requiresReauth((error as MultiFactorError)?.code)
    ) {
      setNeedsReauth(true);
    }
  }, [(error as MultiFactorError)?.code]);

  const handleSubmitPassword = async (data: MfaSmsPasswordEntrySchemaProps) => {
    const { password } = data;
    if (!password) {
      setErrorMessage('Please, confirm your password in order to continue.');
      return;
    }

    try {
      await reauthenticateUser?.(password);
      if (currentUser?.phone_number) {
        const verificationCode = await startEnrollment(currentUser?.phone_number);
        if (verificationCode) {
          setVerificationId(verificationCode || '');
          setNeedsReauth(false);
          setErrorMessage('');
        }
      }
    } catch (error: unknown) {
      const firebaseError = error as FirebaseAuthError;
      if (FirebaseErrorHandler.isCredentialError(firebaseError.code)) {
        setErrorMessage('The provided password is not valid');
      } else {
        setErrorMessage('Something went wrong');
      }
    }
  };

  const handleSubmitCode = async (data: MfaSmsCodeEntrySchemaProps) => {
    const { code } = data;
    if (code) {
      // Handle from login
      if (queryParams.firebaseError) {
        if (challengeResponse) {
          const { resolver, verificationId } = challengeResponse;
          await resolveMfaSignIn(code, verificationId, resolver);
          if (queryParams.forward?.forwardTo) {
            const { forwardTo, forwardParams } = queryParams.forward;
            navigate(forwardTo as any, forwardParams as any);
          }
        }
        // Handle from enrollment
      } else if (queryParams.forward?.forwardTo) {
        const { forwardTo, forwardParams } = queryParams.forward;
        await enrollPhoneMfa(code, verificationId);
        navigate(forwardTo as any, forwardParams as any);
      }
    }
  };

  return (
    <VStack justifyContent="center" alignItems="center" pt={24} gap={4} width="100%">
      {needsReauth && (
        <SmsCodeEntryForm
          flowType={flowType}
          canResend={canResend}
          formType="password"
          handleSubmit={handleSubmitPassword}
          errorMessage={errorMessage}
          loading={loading}
          resendSms={resendSms}
          userPhoneNumber={phoneNumber}
        />
      )}
      {!needsReauth && (
        <SmsCodeEntryForm
          flowType={flowType}
          canResend={canResend}
          formType="code"
          handleSubmit={handleSubmitCode}
          errorMessage={errorMessage}
          loading={loading}
          resendSms={resendSms}
          userPhoneNumber={phoneNumber}
        />
      )}
    </VStack>
  );
};

const SmsCodeEntryForm = ({
  canResend,
  errorMessage,
  formType,
  handleSubmit,
  loading,
  resendSms,
  userPhoneNumber,
  flowType,
}: {
  canResend: boolean;
  errorMessage?: string;
  formType: 'password' | 'code';
  flowType: MFAFlowType;
  handleSubmit:
    | ((data: MfaSmsCodeEntrySchemaProps) => Promise<void>)
    | ((data: MfaSmsPasswordEntrySchemaProps) => Promise<void>);
  loading: boolean;
  resendSms: () => void;
  userPhoneNumber?: string;
}) => {
  const { currentUser } = useCurrentUser();
  const { prevStep, isLoading } = useApplicationMutations();
  const isPasswordForm = formType === 'password';
  const schema = isPasswordForm ? mfaPasswordEntrySchema : mfaSmsCodeEntrySchema;

  const renderSubText = () => {
    if (!!currentUser && !isPasswordForm) {
      return 'We’ve sent a verification code to the following phone number.';
    }

    if (isPasswordForm) {
      return 'To help secure your account, we need to confirm your identity first.';
    }

    return "We've sent a verification code to your phone. Enter the code to proceed.";
  };

  return (
    <Form handleSubmit={handleSubmit} schema={schema}>
      <FixedFooterLayout
        footerContent={
          <HStack p={2} gap={2}>
            {flowType === 'SIGN_UP' &&
              currentUser?.professional?.application_status === 'markets' && (
                <Button loading={isLoading} variant="outlined" fullWidth onClick={() => prevStep()}>
                  Back
                </Button>
              )}
            <SubmitButton fullWidth loading={loading}>
              {isPasswordForm ? 'Confirm Password' : 'Verify code'}
            </SubmitButton>
          </HStack>
        }
      >
        <Box p={2}>
          <Box pb={0.5}>
            <Heading size="s">
              {isPasswordForm ? 'Please confirm your password' : 'Enter your verification code'}
            </Heading>
          </Box>
          <Text size="m" color="text.secondary">
            {renderSubText()}
          </Text>

          {!isPasswordForm && (
            <>
              {userPhoneNumber && (
                <Box pt={2}>
                  <Label size="l">{userPhoneNumber}</Label>
                </Box>
              )}
              <Box pt={2} pl={2}>
                <HelpCenterLink
                  text="Update your phone number"
                  configKey="updatePhoneNumber"
                  color="state.teal.secondary"
                  variant="label"
                />
              </Box>

              <Box mt={2}>
                <NumberField
                  name="code"
                  placeholder="Enter 6-digit PIN"
                  label="Enter code"
                  maxLength={6}
                />
              </Box>

              {canResend && (
                <Box pl={2} mt={2} onClick={resendSms}>
                  <Label size="m" color="state.teal.secondary">
                    Resend code
                  </Label>
                </Box>
              )}
            </>
          )}

          {isPasswordForm && (
            <Box mt={2}>
              <TextField
                name="password"
                placeholder="Enter your password"
                label="Password"
                type="password"
              />
            </Box>
          )}

          <Box py={2}>
            <MfaErrorAlert visible={!!errorMessage} message={errorMessage ? errorMessage : ''} />
          </Box>
        </Box>
      </FixedFooterLayout>
    </Form>
  );
};
